// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import AppShellLayout from '@/components/Layout/AppShellLayout';
import { ConnectorsNavigationProvider } from '@/components/Settings/Connectors/ConnectorsNavigationContext';
import SettingsSidebar from '@/components/Settings/SettingsSidebar';
import { SkillsProvider } from '@/components/Settings/Skills/SkillsProvider';
import { runAfterWorkspaceConfigurationSave } from '@/lib/workspaceConfigurationNavigationGuard';
import { usePageTabStore } from '@/store/pageTabStore';
import type { SettingsSectionId } from '@/store/settingsStore';
import { useCallback } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';

/**
 * The same left rail as Home / Settings, around every sub-page (the product
 * launcher, each product, gateway setup), so the whole app can be navigated
 * from any page without going back first. The rail's open/closed state is the
 * one the top-bar control already drives for Workspace and Home.
 */
export default function SubPageShellLayout() {
  const navigate = useNavigate();
  const sidebarHidden = usePageTabStore(
    (state) => state.workspaceSidebarHidden
  );

  const goHome = useCallback(() => {
    void runAfterWorkspaceConfigurationSave(() => {
      navigate({ pathname: '/home', search: '?section=spaces' });
    });
  }, [navigate]);

  const goSettings = useCallback(
    (section: SettingsSectionId) => {
      void runAfterWorkspaceConfigurationSave(() => {
        navigate({
          pathname: '/home',
          search: `?${new URLSearchParams({ section: 'settings', tab: section })}`,
        });
      });
    },
    [navigate]
  );

  return (
    <SkillsProvider active={false}>
      <ConnectorsNavigationProvider>
        <AppShellLayout
          sidebarHidden={sidebarHidden}
          sidebar={
            <SettingsSidebar
              activeHomeSection={null}
              activeSection={null}
              onHomeSectionChange={goHome}
              onSectionChange={goSettings}
            />
          }
        >
          <main className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden">
            <Outlet />
          </main>
        </AppShellLayout>
      </ConnectorsNavigationProvider>
    </SkillsProvider>
  );
}
